/**
 * Password-rotation orchestration — the activation gate's server round-trip.
 *
 * Same shape as `loginFlow.ts`: composes the pure BFF call (`api/bffAuth.ts`)
 * with a caller-supplied `refetchPortalUser`, so the whole flow — including
 * the BFF's error-code mapping — is testable without a network or React.
 */
import { requestRotatePassword, type FetchLike, type LoginTokens } from '../api/bffAuth.ts';

export type RotateResult =
  /**
   * `reauthenticated: false` means the password IS changed and the account IS
   * activated, but the browser holds a DEAD session: the BFF revokes every
   * session on rotation (the current one included) and could not, or did not,
   * hand back a replacement. The only honest next step is a sign-out to the
   * login screen — silently carrying on is how customers ended up staring at
   * empty service-order screens until they logged out by themselves.
   */
  | { ok: true; reauthenticated: boolean }
  | { ok: false; error: RotateError };

export type RotateError =
  | 'too_short'
  | 'derivable_from_documento'
  | 'common_password'
  | 'session_expired'
  | 'not_activated'
  | 'unknown';

export interface RotateFlowDependencies {
  fetchImpl: FetchLike;
  bffBase: string;
  accessToken: string;
  /** Re-reads `portal_users` after a successful rotation — it now has `activated_at` set. */
  refetchPortalUser: () => Promise<void>;
  /** Installs the fresh session the BFF minted. Must THROW on failure. */
  installSession: (tokens: LoginTokens) => Promise<void>;
}

export async function performRotatePassword(
  newPassword: string,
  deps: RotateFlowDependencies,
): Promise<RotateResult> {
  const outcome = await requestRotatePassword(deps.fetchImpl, deps.bffBase, deps.accessToken, newPassword);

  switch (outcome.kind) {
    case 'transport_error':
      return { ok: false, error: 'unknown' };

    case 'forbidden':
      return { ok: false, error: 'session_expired' };

    case 'rejected':
      // Map stable error codes from the BFF to client-friendly messages.
      if (outcome.code === 'password_too_short') return { ok: false, error: 'too_short' };
      if (outcome.code === 'password_derivable') return { ok: false, error: 'derivable_from_documento' };
      if (outcome.code === 'password_common') return { ok: false, error: 'common_password' };
      return { ok: false, error: 'unknown' };

    case 'ok': {
      // FIRST, replace the session. The token this request was made with was
      // revoked server-side as part of the rotation; everything below and
      // every BFF call afterwards needs the new one.
      let reauthenticated = false;
      if (outcome.session) {
        try {
          await deps.installSession(outcome.session);
          reauthenticated = true;
        } catch {
          // Fall through with reauthenticated=false: the caller signs out.
        }
      }

      // Re-fetch the portal_user row — it now has activated_at set.
      //
      // Deliberately non-fatal, unlike the pre-split behaviour that wrapped
      // this whole flow (BFF call + refetch) in one try/catch and returned
      // `{ ok: false, error: 'unknown' }` on any failure here. By this point
      // the rotation has already SUCCEEDED on the server — the password is
      // changed. Reporting "unknown error" to the customer would be a lie
      // that pushes them to retry rotation with a credential that no longer
      // works. A failed refresh only means our local `portal_users` shadow
      // row is briefly stale (`activated_at` not yet reflected client-side);
      // it self-heals on the next auth event that reads it. So: swallow the
      // failure, still report the rotation itself as `ok`.
      try {
        await deps.refetchPortalUser();
      } catch {
        // Degraded-but-recoverable — see comment above. Nothing to do here.
      }
      return { ok: true, reauthenticated };
    }
  }
}
